import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Tooltip } from '../../ui';
import IronSightMark from '../../shared/IronSightMark';
import { UnitChart, niceTicks } from '../charts';
import { tabCtxShape } from './tabContext';
import { peerSummaries } from '../../../utils/peerSummaries';
import { formatCurrency, formatDate, formatNumber, getBuyerHeatStyle } from '../../../utils/formatting';
import { aiPriceGapDisplay, consistentAiRange } from '../../../utils/aiPrice';
import { useDealershipSettings } from '../../../contexts/settingsContext';
import useMarginFloorPct from '../../../hooks/useMarginFloor';
import { ANOMALY_GROUPS, anomalyReasonText } from '../../../utils/anomalySignals';
import { daysToMedianSale, riskMeter } from '../../../utils/riskMeter';
import { cachedLabel } from '../../../utils/askIronSight';
import { RISK_TIER_LABELS } from '../../../utils/inventoryView';
import SOURCE_LABELS from '../../../config/sourceLabels';
import {
    activeQuotesFor, advertisedVsInternal, anomalyContext, buildTrajectory, carryingRatePct, carryingToSale,
    calcMarginTone, completenessTone, grossMarginGood, hasAnomaly, hasAnyInsight, hasQuoteInterest, hasSellSpeedData,
    listingAgeTone, marginCalc, peerComparison, riskRecommendation, saleCountdown, survivalPoints, trajectoryCaption,
} from '../../../utils/unitIntelligence';
import { CheckCircleIcon, WarnIcon } from '../icons';
import NotForCommitted from '../NotForCommitted';
import './IntelligenceTabV2.css';

const SEVERITY_TONE = { critical: 'crit', warning: 'warn', positive: 'pos' };
const TIER_TONE = { Red: 'neg', Yellow: 'warn', Green: 'pos' };
// Buyer heat (today's Hot ≤7d / Warm ≤30d / Cold ≤90d): bars and tone.
const HEAT_LEVEL = { Hot: 3, Warm: 2, Cold: 1, 'No Interest': 0 };
const HEAT_TONE = { Hot: 'hot', Warm: 'warm', Cold: 'cold', 'No Interest': 'none' };
const RISK_PILL = { Red: 'red', Yellow: 'amber', Green: 'sage' };
// Today's four peer bands as v2 tones (in line = calm, no alarm).
const BAND_TEXT = { low: 'u2-pos', inline: 'u2-calm', high: 'u2-warn', veryHigh: 'u2-neg' };
const BAND_BAR = { low: 'sage', inline: 'slate', high: 'amber', veryHigh: 'red' };
// Gap state → text tone (same states as the table chip).
const GAP_TONE = { inline: 'u2-pos', above: 'u2-warn', wellAbove: 'u2-neg', below: 'u2-info', review: 'u2-quiet' };
const AGE_TONE = { pos: 'u2-pos', inline: 'u2-calm', warn: 'u2-warn', neg: 'u2-neg', quiet: 'u2-quiet' };
const money = (v) => formatCurrency(v);
const shortK = (v) => `$${Math.round(v / 1000)}k`;

function Mark() {
    return <span className="u2-is-mark"><IronSightMark /> IronSight</span>;
}

// ── Anomaly (P-UNIT-027) ─────────────────────────────────────────────────
function AnomalyCard({ unit }) {
    const tone = SEVERITY_TONE[unit.AnomalySeverity];
    if (!tone) return null;
    const group = ANOMALY_GROUPS.find((g) => g.verdicts.includes(unit.AnomalyVerdict))?.label;
    const chips = anomalyContext(unit);
    return (
        <div className={`u2i-anom u2i-anom--${tone}`} role="note">
            <div className="u2i-anom-hd">
                {tone === 'pos' ? <CheckCircleIcon /> : <WarnIcon />}
                {unit.AnomalyVerdict}
                {group && <span className="u2i-anom-grp">{group}</span>}
            </div>
            {anomalyReasonText(unit) && <p>{anomalyReasonText(unit)}</p>}
            {chips.length > 0 && (
                <div className="u2i-ctxchips">{chips.map((c) => <span key={c}>{c}</span>)}</div>
            )}
        </div>
    );
}
AnomalyCard.propTypes = { unit: PropTypes.object.isRequired };

// ── Days-to-sell forecast (P-UNIT-030) ───────────────────────────────────
const DAYS_PER_MONTH = 30.44;
const inRange = (m, months) => m != null && Number.isFinite(m) && m >= 0 && m <= months;

/**
 * The prototype's shaded "typical" band (RangeLowDays–RangeHighDays, the
 * middle half of outcomes) on the monthly axis, when both ends are known
 * (QA visual 12). Today's fields, today's tooltip wording.
 */
export const forecastBands = (unit, months) => {
    const lo = unit.RangeLowDays != null ? unit.RangeLowDays / DAYS_PER_MONTH : null;
    const hi = unit.RangeHighDays != null ? unit.RangeHighDays / DAYS_PER_MONTH : null;
    if (lo == null || hi == null || !(hi > lo) || lo > months) return [];
    return [{ from: lo, to: Math.min(hi, months), label: `typical ${unit.RangeLowDays}–${unit.RangeHighDays}d` }];
};

/** The median marker and the "9 in 10 by Xd" slow-case marker (UpperDays). */
export const forecastRefs = (unit, months) => {
    const refs = [];
    const median = unit.MedianDays != null ? unit.MedianDays / DAYS_PER_MONTH : null;
    if (inRange(median, months)) refs.push({ x: median, label: `median ${unit.MedianDays}d`, tone: 'grey' });
    const upper = unit.UpperDays != null ? unit.UpperDays / DAYS_PER_MONTH : null;
    if (inRange(upper, months)) refs.push({ x: upper, label: `9 in 10 by ${unit.UpperDays}d`, tone: 'grey' });
    return refs;
};

function ForecastCard({ unit }) {
    const points = survivalPoints(unit);
    const carry = carryingToSale(unit);
    const months = points ? points.labels.length - 1 : 0;
    const tip = (
        <>
            <b>How long until this unit sells.</b> An AI survival model, trained on years of past sales, estimates the
            chance of selling by each future month — that&apos;s the curve (0% today, rising toward 100%).
            {' '}<b>Typical range ({unit.RangeLowDays}–{unit.RangeHighDays}d)</b> — the middle half of likely outcomes (25th–75th percentile).
            {' '}<b>Median (~{unit.MedianDays}d)</b> — where the curve crosses 50%: half of similar units sell faster, half slower.
            {' '}<b>Up to ~{unit.UpperDays}d if slow</b> — a calibrated slow-case bound: about 9 in 10 sell by then, the rest take longer.
            {' '}<b>The % tiles</b> read the curve at 30 / 60 / 90 / 180 days — e.g. &quot;{unit.PSoldBy90 != null ? `${unit.PSoldBy90.toFixed(0)}% sold by 90d` : 'sold by 90d'}&quot;.
            {' '}<b>Est. carrying to sale</b> = median days × this unit&apos;s daily floor-plan cost.
            {' '}Percentages are calibrated on held-out sales, so they reflect what actually happened, not just model
            confidence. Sell-time is skewed — most go quickly, a few drag on — so we show a range, never one exact day.
        </>
    );
    return (
        <div className="u2-card">
            <div className="u2-c-head">
                <Mark />
                <h3>Days-to-sell forecast</h3>
                <Tooltip label="How to read the forecast" content={tip} />
                {unit.ExpectedSaleDate && (
                    <span className="u2-right"><span className="u2i-fc-date">Expected sale ≈ {formatDate(unit.ExpectedSaleDate)}</span></span>
                )}
            </div>
            <div className="u2i-fc-head">
                <span className="u2i-fc-range">{unit.RangeLowDays}–{unit.RangeHighDays} <small>days</small></span>
                <span className="u2i-fc-sub">typical · median ~{unit.MedianDays}d · up to ~{unit.UpperDays}d if slow</span>
            </div>
            {points && (
                <UnitChart
                    label={`Chance sold by month: ${points.labels.slice(1).map((l, i) => `${points.soldPct[i + 1]}% by ${l}`).join(', ')}.`}
                    height={210}
                    pad={{ l: 44 }}
                    x={[0, months]}
                    y={[0, 100]}
                    series={points.soldPct.map((v, i) => [i, v])}
                    area
                    yticks={[[0, '0%'], [50, '50%'], [100, '100%']]}
                    xticks={points.labels.map((l, i) => [i, l]).filter(([i]) => i === 0 || i % 3 === 0 || i === months)}
                    vbands={forecastBands(unit, months)}
                    refs={forecastRefs(unit, months)}
                    readout={(i) => `${points.soldPct[i]}% sold by ${points.labels[i]}`}
                    table={points.labels.map((l, i) => [l, `${points.soldPct[i]}% sold`])}
                />
            )}
            <div className="u2i-probs">
                {[['30d', unit.PSoldBy30], ['60d', unit.PSoldBy60], ['90d', unit.PSoldBy90], ['180d', unit.PSoldBy180]].map(([lbl, val]) => (
                    <div key={lbl}>
                        <b>{val != null ? `${val.toFixed(0)}%` : '—'}</b>
                        <span>sold by {lbl}</span>
                    </div>
                ))}
            </div>
            {carry != null && (
                <div className="u2i-fc-carry">
                    Est. carrying to sale <b>{money(carry)}</b>
                    <span className="u2-muted">({unit.MedianDays}d × {money(unit.CarryingCostDaily)}/day)</span>
                </div>
            )}
        </div>
    );
}
ForecastCard.propTypes = { unit: PropTypes.object.isRequired };

// ── 12-month trajectory (P-UNIT-033) ─────────────────────────────────────
function TrajectoryCard({ unit, trajectory: t }) {
    const values = [...t.prices, ...(t.bandLow || []), ...(t.bandHigh || [])];
    const asking = unit.Best_Sell_Price > 0 ? unit.Best_Sell_Price : null;
    const scale = niceTicks(Math.min(...values), Math.max(...values, ...(asking ? [asking] : [])), 3);
    const last = t.prices.length - 1;
    const down = t.totalDrop > 0;
    const monthlyDown = t.monthlyAvg > 0;
    return (
        <div className="u2-card">
            <div className="u2-c-head">
                <Mark />
                <h3>12-month price trajectory</h3>
                <span className="u2-sub">{trajectoryCaption(t)}</span>
            </div>
            <UnitChart
                label={`Predicted resale value from ${money(t.firstPrice)} today to ${money(t.lastPrice)} at month 12.`}
                height={190}
                x={[0, last]}
                y={[scale.lo, scale.hi]}
                series={t.prices.map((v, i) => [i, v])}
                band={t.hasBand ? t.prices.map((_, i) => [i, t.bandLow[i], t.bandHigh[i]]) : null}
                refs={asking ? [{ y: asking, label: `Asking ${shortK(asking)}`, tone: 'soft' }] : []}
                yticks={scale.ticks.map((v) => [v, shortK(v)])}
                xticks={t.labels.map((l, i) => [i, l]).filter(([i]) => i === 0 || i % 3 === 0 || i === last)}
                readout={(i) => {
                    const base = `${t.labels[i]} · ${money(t.prices[i])}`;
                    return t.hasBand ? `${base} · Optimistic ${money(t.bandHigh[i])} / Conservative ${money(t.bandLow[i])}` : base;
                }}
                table={t.labels.map((l, i) => [l, money(t.prices[i])])}
            />
            <dl className="u2-kv">
                <dt>Today</dt><dd>{money(t.firstPrice)}</dd>
                <dt>Month 12</dt><dd>{money(t.lastPrice)}</dd>
                <dt>{down ? 'Total Depreciation' : 'Total Appreciation'}</dt>
                <dd className={down ? 'u2-neg' : 'u2-pos'}>
                    {down ? '-' : '+'}{money(Math.abs(t.totalDrop))} ({down ? '-' : '+'}{Math.abs(t.dropPct).toFixed(1)}%)
                </dd>
                <dt>{monthlyDown ? 'Avg Monthly Loss' : 'Avg Monthly Gain'}</dt>
                <dd className={monthlyDown ? 'u2-neg' : 'u2-pos'}>{monthlyDown ? '-' : '+'}{money(Math.abs(t.monthlyAvg))}/mo</dd>
            </dl>
        </div>
    );
}
TrajectoryCard.propTypes = { unit: PropTypes.object.isRequired, trajectory: PropTypes.object.isRequired };

// ── Carrying cost + margin calculator (P-UNIT-037..041) ─────────────────
// The calculator's gross margin follows the margin floor (QA S-10).
const CALC_TONE = { good: 'u2-pos', warn: 'u2-warn', neg: 'u2-neg' };

function CarryingCard({ unit, calcPrice, setCalcPrice, activeQuotes }) {
    // The dealership's floor plan APR and margin floor (7% / 10% by default).
    const { aprPct } = useDealershipSettings();
    // The signed-in user's floor (personal override within the dealership range).
    const marginFloorPct = useMarginFloorPct();
    if (unit.GrossMargin == null) return null;
    const pending = unit.StockStatus === 'P';
    const inStock = unit.StockStatus === 'S';
    const good = grossMarginGood(unit.GrossMarginPct, marginFloorPct);
    const calc = marginCalc(unit, calcPrice, activeQuotes);
    const liveQuote = activeQuotes.length ? Math.max(...activeQuotes.map((q) => q.selling_price)) : null;
    const fills = [
        ['Asking', unit.Best_Sell_Price || null],
        ['Live quote', liveQuote],
        ['AI price', unit.PredictedPrice > 0 ? unit.PredictedPrice : null],
        ['True cost', unit.CarryingCostBasis],
    ].filter(([, v]) => v != null);
    const carryTip = pending ? (
        <>
            <b>Pending unit — not yet on books.</b> Gross Margin = Best Sell Price − Cost Estimate (Cost Estimate = Sales
            Book Value + planned reconditioning). Carrying cost is $0 — no money has been committed yet.
        </>
    ) : (
        <>
            <b>In-Stock unit — on the books.</b> Gross Margin = Best Sell Price − True Cost. True Cost = Wholesale + Retail
            + Access + After-Market + Charge + Option + Other + Paint + Pre-Delivery + Repair + Surcharge + Trim.
            {' '}Daily Carrying Cost = True Cost × {carryingRatePct(unit, aprPct)}% APR ÷ 365
        </>
    );
    const calcTip = (
        <>
            <b>How this is calculated.</b> <b>Gross Margin</b>: Sale Price − True Cost ({money(unit.CarryingCostBasis)}),
            changes as you adjust the price. <b>If accepted today</b>: Gross Margin − {money(unit.CarryingCostToDate)} already
            accrued ({unit.DaysInStock} days × {money(unit.CarryingCostDaily)}/day). This is the net you walk away with if
            you take the offer right now.
            {calc.holdAtSale != null && (
                <>
                    {' '}<b>If held to predicted sale</b>: always based on the original asking price ({money(unit.Best_Sell_Price)}):
                    Original Gross Margin − Total Exposure ({money(unit.CarryingCostTotalExposure)}) = {money(unit.CarryingCostToDate)} accrued
                    {' '}+ {money(unit.CarryingCostProjected)} projected (~{Math.round(unit.MedianDays)}d more). Use this to decide if the
                    offer is better than waiting.
                </>
            )}
        </>
    );
    const tone = (v) => (v >= 0 ? 'u2-pos' : 'u2-neg');
    return (
        <div className="u2-card" id="u2-carry">
            <div className="u2-c-head">
                <h3>Estimated carrying cost</h3>
                <Tooltip label="How carrying cost is calculated" content={carryTip} />
                <span className="u2-sub">floor plan on the {pending ? 'cost estimate' : 'true cost'}</span>
            </div>
            <div className="u2i-formula" aria-label="Gross margin formula">
                <span><small>Asking Price</small><b>{money(unit.Best_Sell_Price)}</b></span>
                <i aria-hidden="true">−</i>
                <span><small>{pending ? 'Cost Estimate' : 'True Cost'}</small><b>{money(unit.CarryingCostBasis)}</b></span>
                <i aria-hidden="true">=</i>
                <span>
                    <small>Gross Margin</small>
                    <b className={good ? 'u2-pos' : 'u2-warn'}>{money(unit.GrossMargin)}</b>
                    <small className={good ? 'u2-pos' : 'u2-warn'}>{unit.GrossMarginPct?.toFixed(1)}%</small>
                </span>
            </div>
            <div className="u2-tiles3">
                <div className="u2-tile">
                    <span className="u2-tile-l">Daily carrying cost</span>
                    {pending ? (
                        <><span className="u2-tile-n u2-pos">$0</span><span className="u2-tile-s">Not yet on books</span></>
                    ) : (
                        <>
                            <span className="u2-tile-n">{money(unit.CarryingCostDaily)}<small>/day</small></span>
                            <span className="u2-tile-s">{carryingRatePct(unit, aprPct)}% APR on {money(unit.CarryingCostBasis)}</span>
                        </>
                    )}
                </div>
                <div className="u2-tile">
                    <span className="u2-tile-l">Carrying cost to date</span>
                    <span className={`u2-tile-n ${pending ? 'u2-pos' : 'u2-warn'}`}>{pending ? '$0' : money(unit.CarryingCostToDate)}</span>
                    <span className="u2-tile-s">{unit.DaysInStock} days in stock</span>
                </div>
                {unit.CarryingCostProjected != null && (
                    <div className="u2-tile">
                        <span className="u2-tile-l">Projected to sale</span>
                        <span className="u2-tile-n u2-neg">{money(unit.CarryingCostProjected)}</span>
                        <span className="u2-tile-s">~{Math.round(unit.MedianDays)}d more to sell</span>
                    </div>
                )}
                {unit.CarryingCostTotalExposure != null && (
                    <div className="u2-tile">
                        <span className="u2-tile-l">Total exposure</span>
                        <span className="u2-tile-n u2-neg">{money(unit.CarryingCostTotalExposure)}</span>
                        <span className="u2-tile-s">{unit.DaysInStock + Math.round(unit.MedianDays)} total days</span>
                    </div>
                )}
                {unit.MarginAfterCarrying != null && inStock && (
                    <div className="u2-tile">
                        <span className="u2-tile-l">Remaining margin today</span>
                        <span className={`u2-tile-n ${tone(unit.MarginAfterCarrying)}`}>{money(unit.MarginAfterCarrying)}</span>
                        <span className="u2-tile-s">
                            After {money(unit.CarryingCostToDate)} carrying to date
                            {unit.MarginAfterCarryingPct != null && ` · ${unit.MarginAfterCarryingPct.toFixed(1)}%`}
                        </span>
                    </div>
                )}
                {unit.MarginAtSale != null && inStock && (
                    <div className="u2-tile">
                        <span className="u2-tile-l">Margin at predicted sale date</span>
                        <span className={`u2-tile-n ${tone(unit.MarginAtSale)}`}>{money(unit.MarginAtSale)}</span>
                        <span className="u2-tile-s">
                            After {money(unit.CarryingCostTotalExposure)} total exposure
                            {unit.MarginAtSalePct != null && ` · ${unit.MarginAtSalePct.toFixed(1)}%`}
                        </span>
                    </div>
                )}
            </div>

            {unit.CarryingCostBasis != null && (
                <div className="u2i-calc">
                    <div className="u2-c-head">
                        <h4>Margin calculator</h4>
                        <Tooltip label="How the calculator works" content={calcTip} />
                        <span className="u2-sub">True cost {money(unit.CarryingCostBasis)} · Carrying {money(unit.CarryingCostToDate)} accrued</span>
                    </div>
                    <div className="u2i-calc-in">
                        <label htmlFor="u2-calc-price">Sale price</label>
                        <span className="u2i-money-in">
                            <span aria-hidden="true">$</span>
                            <input
                                id="u2-calc-price"
                                type="number"
                                value={calc.price}
                                min={0}
                                step={1000}
                                onChange={(e) => setCalcPrice(Number(e.target.value))}
                            />
                        </span>
                        <span className="u2i-quick" role="group" aria-label="Fill with">
                            {fills.map(([label, value]) => (
                                <button
                                    key={label}
                                    type="button"
                                    aria-pressed={calcPrice != null && calcPrice === value}
                                    onClick={() => setCalcPrice(value)}
                                >
                                    {label}
                                </button>
                            ))}
                        </span>
                        {calcPrice !== null && (
                            <button type="button" className="u2-linkb" onClick={() => setCalcPrice(null)}>Reset</button>
                        )}
                    </div>
                    <div className="u2i-calc-res" aria-live="polite">
                        <div className="u2-tile">
                            <span className="u2-tile-l">Gross margin</span>
                            <span className={`u2-tile-n ${CALC_TONE[calcMarginTone(calc.calcGross, calc.calcGrossPct, marginFloorPct)]}`}>{money(calc.calcGross)}</span>
                            <span className="u2-tile-s">{calc.calcGrossPct.toFixed(1)}% · before carrying cost</span>
                        </div>
                        {unit.CarryingCostToDate != null && (
                            <div className="u2-tile">
                                <span className="u2-tile-l">If accepted today</span>
                                <span className={`u2-tile-n ${tone(calc.calcRemaining)}`}>{money(calc.calcRemaining)}</span>
                                <span className="u2-tile-s">after {money(unit.CarryingCostToDate)} accrued</span>
                            </div>
                        )}
                        {calc.holdAtSale != null && (
                            <div className="u2-tile">
                                <span className="u2-tile-l">If held to predicted sale</span>
                                <span className={`u2-tile-n ${tone(calc.holdAtSale)}`}>{money(calc.holdAtSale)}</span>
                                <span className="u2-tile-s">~{Math.round(unit.MedianDays)}d more at {money(unit.Best_Sell_Price)}</span>
                            </div>
                        )}
                    </div>
                    {calcPrice != null && calc.higherQuotes.length > 0 && (
                        <div className="u2-higher" role="status">
                            <b>{calc.higherQuotes.length} active quote{calc.higherQuotes.length !== 1 ? 's' : ''} at or above {money(calcPrice)}</b>
                            <ul className="u2i-quotes">
                                {calc.higherQuotes.map((q, i) => (
                                    <li key={q.quote_id || i}>
                                        <b>{money(q.selling_price)}</b>
                                        {q.buyer_name && <span>{q.buyer_name}</span>}
                                        {q.salesperson && <span>{q.salesperson}</span>}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {calcPrice != null && activeQuotes.length > 0 && calc.higherQuotes.length === 0 && (
                        <p className="u2-note u2i-noquote">No active quotes at or above {money(calcPrice)}</p>
                    )}
                </div>
            )}
        </div>
    );
}
CarryingCard.propTypes = {
    unit: PropTypes.object.isRequired,
    calcPrice: PropTypes.number,
    setCalcPrice: PropTypes.func.isRequired,
    activeQuotes: PropTypes.array.isRequired,
};

// ── U6 Ask IronSight teaser (N13) ────────────────────────────────────────
function AskTeaser({ detail, goTab }) {
    const { agentState, fastTrackAgent: agent } = detail;
    const recs = agent?.recommendations?.length || 0;
    const cached = cachedLabel(agent);
    return (
        <div className="u2-card u2-card--ai">
            <div className="u2-c-head">
                <span className="u2-is-mark"><IronSightMark /> Ask IronSight</span>
                {agentState === 'ready' && cached && <span className="u2-sub">{cached}</span>}
            </div>
            {agentState === 'ready' && agent ? (
                <>
                    <p className="u2i-teaser">{agent.headline}</p>
                    <button type="button" className="u2-linkb" onClick={() => goTab('fasttrack')}>
                        Read the full answer{recs ? ` and ${recs} recommendation${recs === 1 ? '' : 's'}` : ''} →
                    </button>
                </>
            ) : agentState === 'error' ? (
                <>
                    <p className="u2-note" role="alert">{detail.fastTrackError || 'Could not generate recommendations. Please try again.'}</p>
                    <Button size="sm" onClick={detail.regenerateAgent}>Try again</Button>
                </>
            ) : (
                <p className="u2-note u2i-preparing" role="status">Ask IronSight is preparing its answer…</p>
            )}
        </div>
    );
}
AskTeaser.propTypes = { detail: PropTypes.object.isRequired, goTab: PropTypes.func.isRequired };

// ── Sell-speed risk + D8 meter (P-UNIT-029) ──────────────────────────────
function RiskCard({ unit, allUnits }) {
    const meter = riskMeter(unit, allUnits);
    const label = RISK_TIER_LABELS[unit.RiskTier];
    const tone = TIER_TONE[unit.RiskTier] || 'quiet';
    return (
        <div className="u2-card">
            <div className="u2-c-head"><Mark /><h3>Sell-speed risk</h3></div>
            <div className="u2i-riskbig">
                <span className={`u2i-risk-v u2-${tone}`}>
                    {unit.LadenhueterRisk != null ? `${unit.LadenhueterRisk.toFixed(0)}%` : '—'}
                </span>
                <span className="u2i-risk-lab">
                    slow-mover risk
                    <span className={`inv2-risk inv2-risk--${RISK_PILL[unit.RiskTier] || 'grey'}`}><i aria-hidden="true" />{label} Risk</span>
                </span>
            </div>
            {meter && (
                <>
                    <div
                        className="u2i-meter"
                        role="img"
                        aria-label={`Risk meter: ${label} zone. Zones from the model's calibrated tier cuts: Low below ${meter.cuts[0]}%, Medium to ${meter.cuts[1]}%, High above.`}
                    >
                        <span className="u2i-zone u2i-zone--low" style={{ width: `${meter.cuts[0]}%` }} />
                        <span className="u2i-zone u2i-zone--mid" style={{ width: `${meter.cuts[1] - meter.cuts[0]}%` }} />
                        <span className="u2i-zone u2i-zone--high" style={{ width: `${100 - meter.cuts[1]}%` }} />
                        <em className="u2i-mk" style={{ left: `${meter.marker}%` }} />
                    </div>
                    <div className="u2i-meter-l" aria-hidden="true">
                        <span style={{ width: `${meter.cuts[0]}%` }}>Low</span>
                        <span style={{ width: `${meter.cuts[1] - meter.cuts[0]}%` }}>Medium</span>
                        <span style={{ width: `${100 - meter.cuts[1]}%` }}>High</span>
                    </div>
                </>
            )}
            <p className={`u2i-rec u2i-rec--${tone}`}><b>Recommendation:</b> {riskRecommendation(unit.RiskTier)}</p>
        </div>
    );
}
RiskCard.propTypes = { unit: PropTypes.object.isRequired, allUnits: PropTypes.array };

// ── Price intelligence (P-UNIT-032, N15, D8 tag) ─────────────────────────
function PriceIntelCard({ unit, liveQuote }) {
    const { gapThresholds } = useDealershipSettings();
    const ai = unit.PredictedPrice;
    const asking = unit.Best_Sell_Price;
    const gap = aiPriceGapDisplay(unit, gapThresholds);
    const range = consistentAiRange(unit);
    const countdown = saleCountdown(unit.PredictedSaleDate);
    const median = daysToMedianSale(unit);
    const marks = [ai, asking, liveQuote, range?.low, range?.high].filter((v) => v != null && v > 0);
    const lo = Math.min(...marks);
    const hi = Math.max(...marks);
    const padSpan = (hi - lo) * 0.08 || hi * 0.05 || 1;
    const X = (v) => `${(((v - (lo - padSpan)) / ((hi + padSpan) - (lo - padSpan))) * 100).toFixed(2)}%`;
    return (
        <div className="u2-card">
            <div className="u2-c-head"><Mark /><h3>Price intelligence</h3></div>
            <div className="u2i-pi-row">
                <div><span className="u2i-pi-l">Asking Price</span><span className="u2i-pi-n">{money(asking)}</span></div>
                <span className="u2i-pi-vs">vs</span>
                <div><span className="u2i-pi-l">IronSight Estimate</span><span className="u2i-pi-n u2-pos">{money(ai)}</span></div>
            </div>
            <div className="u2i-scale" aria-hidden="true">
                <div className="u2i-trk" />
                {range && <div className="u2i-rng" style={{ left: X(range.low), width: `calc(${X(range.high)} - ${X(range.low)})` }} />}
                <div className="u2i-m u2i-m--ai" style={{ left: X(ai) }} />
                {liveQuote != null && <div className="u2i-m u2i-m--q" style={{ left: X(liveQuote) }} />}
                <div className="u2i-m u2i-m--ask" style={{ left: X(asking) }} />
            </div>
            {range && (
                <div className="u2i-scale-l" aria-hidden="true">
                    <span style={{ left: X(range.low) }}>{shortK(range.low)}</span>
                    <span style={{ left: X(range.high) }}>{shortK(range.high)}</span>
                </div>
            )}
            <div className="u2-legend">
                <span className="u2-tone-ink"><i />Asking</span>
                <span className="u2-tone-sage"><i />AI price</span>
                {liveQuote != null && <span className="u2-tone-teal"><i />Live quote</span>}
                {range && <span><i className="u2i-lg-rng" />Likely range</span>}
            </div>
            {gap && (
                <p className={`u2i-gap ${GAP_TONE[gap.state] || ''}`}>
                    <b>{gap.pctText}</b> {gap.label}
                </p>
            )}
            {unit.PredictedSaleDate && (
                <div className="u2i-pred-row">
                    <span>Prediction made on <b>{formatDate(unit.PredictedSaleDate)}</b></span>
                    {countdown && <span className={`u2i-cd${countdown.tone ? ` u2i-cd--${countdown.tone}` : ''}`}>{countdown.text}</span>}
                    {median != null && <span className="u2-tag">{median}d to median sale</span>}
                </div>
            )}
            {!unit.PredictedSaleDate && median != null && (
                <div className="u2i-pred-row"><span className="u2-tag">{median}d to median sale</span></div>
            )}
        </div>
    );
}
PriceIntelCard.propTypes = { unit: PropTypes.object.isRequired, liveQuote: PropTypes.number };

// ── Buyer interest (P-UNIT-031, N17) ─────────────────────────────────────
function BuyerInterestCard({ unit, goTab }) {
    const heat = getBuyerHeatStyle(unit.QuoteCount, unit.DaysSinceLastQuote);
    return (
        <div className="u2-card">
            <div className="u2-c-head"><Mark /><h3>Buyer interest</h3></div>
            <div className="u2i-row">
                <span className={`u2i-heat u2i-heat--${HEAT_TONE[heat.label]}`}>
                    <span className="inv2-bars" aria-hidden="true">
                        {[1, 2, 3].map((i) => <span key={i} className={i <= HEAT_LEVEL[heat.label] ? 'on' : undefined} />)}
                    </span>
                    {heat.label}
                </span>
                <span className="u2-note">
                    <b>{unit.QuoteCount || 0}</b> quote{unit.QuoteCount !== 1 ? 's' : ''}
                    {' · '}
                    {unit.DaysSinceLastQuote != null ? `Last quoted ${unit.DaysSinceLastQuote}d ago` : 'Never quoted'}
                </span>
                <button type="button" className="u2-linkb u2i-push" onClick={() => goTab('pipeline')}>Pipeline</button>
            </div>
        </div>
    );
}
BuyerInterestCard.propTypes = { unit: PropTypes.object.isRequired, goTab: PropTypes.func.isRequired };

// ── Against peers (P-UNIT-034..036) ─────────────────────────────────────
function PeerBar({ title, value, avgText, countText, cmp, rangeText }) {
    return (
        <div className="u2i-pbar">
            <div className="u2i-pbar-top">
                <span className="u2-muted">{title}</span>
                <b>{value}</b>
                <span className="u2-muted">vs {avgText} avg ({countText})</span>
                <span className={`u2i-vd ${BAND_TEXT[cmp.band]}`}>{Math.round(cmp.pct)}% of avg</span>
            </div>
            <div className="u2i-tr"><span className={`u2-tone-${BAND_BAR[cmp.band]}`} style={{ width: `${cmp.barWidth}%` }} /><i className="u2i-avg" /></div>
            <div className="u2i-rg">{rangeText} · <span className={BAND_TEXT[cmp.band]}>{cmp.verdict}</span></div>
        </div>
    );
}
PeerBar.propTypes = {
    title: PropTypes.string, value: PropTypes.node, avgText: PropTypes.string, countText: PropTypes.string, cmp: PropTypes.object, rangeText: PropTypes.string,
};

function PeersCard({ unit, comps, goTab }) {
    // Peer averages without the unit itself (utils/marketComps peerSummaries).
    const peerStats = peerSummaries(comps, unit.StockNumber);
    const listing = peerStats.listing_duration_summary;
    const hours = peerStats.hours_summary;
    const lc = listing && unit.MFDaysListed != null ? peerComparison(unit.MFDaysListed, listing, 'listing') : null;
    const hc = hours && unit.ODOMETER != null ? peerComparison(unit.ODOMETER, hours, 'hours') : null;
    if (!lc && !hc) return null;
    return (
        <div className="u2-card">
            <div className="u2-c-head"><h3>Against peers</h3><span className="u2-sub">active peers, same make/model/year</span></div>
            <div className="u2i-split">
                {lc && (
                    <PeerBar
                        title="Listing Duration vs Peers"
                        value={`${unit.MFDaysListed}d`}
                        avgText={`${Math.round(listing.avg)}d`}
                        countText={`${listing.count} listed`}
                        cmp={lc}
                        rangeText={`${Math.round(listing.avg)}d avg · ${Math.round(listing.min)}d shortest · ${Math.round(listing.max)}d longest`}
                    />
                )}
                {hc && (
                    <PeerBar
                        title="Operation Hours vs Peers"
                        value={`${formatNumber(unit.ODOMETER)}h`}
                        avgText={`${formatNumber(Math.round(hours.avg))}h`}
                        countText={`${hours.count} units`}
                        cmp={hc}
                        rangeText={`${formatNumber(Math.round(hours.avg))}h avg · ${formatNumber(Math.round(hours.min))}h lowest · ${formatNumber(Math.round(hours.max))}h highest`}
                    />
                )}
            </div>
            <button type="button" className="u2-linkb" onClick={() => goTab('comps')}>View active peers in Comps →</button>
        </div>
    );
}
PeersCard.propTypes = { unit: PropTypes.object.isRequired, comps: PropTypes.object, goTab: PropTypes.func.isRequired };

// ── Listing insights (P-UNIT-042, N19) ───────────────────────────────────
function ListingInsightsCard({ unit, mfData, listingUrl }) {
    const adv = unit.Best_Sell_Price ? advertisedVsInternal(mfData.advertised_price, unit.Best_Sell_Price) : null;
    return (
        <div className="u2-card">
            <div className="u2-c-head">
                <h3>{SOURCE_LABELS.listingInsights}</h3>
                {listingUrl && (
                    <span className="u2-right">
                        <a className="u2-linkb" href={listingUrl} target="_blank" rel="noopener noreferrer">Open listing</a>
                    </span>
                )}
            </div>
            <div className="u2-tiles3">
                {mfData.advertised_price != null && (
                    <div className="u2-tile">
                        <span className="u2-tile-l">Advertised Price</span>
                        <span className="u2-tile-n">{money(mfData.advertised_price)}</span>
                        {adv && <span className={`u2-tile-s u2-${adv.tone}`}>{adv.text}</span>}
                    </div>
                )}
                {mfData.days_listed != null && (
                    <div className="u2-tile">
                        <span className="u2-tile-l">Listed for</span>
                        <span className={`u2-tile-n ${AGE_TONE[listingAgeTone(mfData.days_listed)]}`}>{mfData.days_listed} days</span>
                        {mfData.days_since_modified != null && <span className="u2-tile-s">Last updated {mfData.days_since_modified}d ago</span>}
                    </div>
                )}
                <div className="u2-tile">
                    <span className="u2-tile-l">Listing Quality</span>
                    <span className={`u2-tile-n ${AGE_TONE[completenessTone(mfData.completeness_pct)]}`}>{mfData.completeness_score}/6</span>
                    {mfData.missing_fields?.length > 0 && <span className="u2-tile-s">Missing: {mfData.missing_fields.join(', ')}</span>}
                </div>
            </div>
        </div>
    );
}
ListingInsightsCard.propTypes = { unit: PropTypes.object.isRequired, mfData: PropTypes.object.isRequired, listingUrl: PropTypes.string };

const LoadingLine = ({ children }) => <div className="u2-card u2i-loading" role="status">{children}</div>;
LoadingLine.propTypes = { children: PropTypes.node };

/**
 * Intelligence (tab 'ai'): today's AITab and CarryingCostSection in the
 * prototype's two columns — forecasts and money left, scores and peer
 * context right — plus the Ask IronSight teaser (U6), the calibrated risk
 * meter and the "Nd to median sale" tag (D8). All logic and wording from
 * utils/unitIntelligence (shared with today's pane).
 */
export default function IntelligenceTabV2({ ctx }) {
    const {
        unit, detail, goTab, allUnits, committed,
    } = ctx;
    const {
        mfData, mfLoading, comps, pipeline, quoteEquipment, predictions12m, predictions12mLoading, calcPrice, setCalcPrice,
    } = detail;
    const trajectory = useMemo(() => buildTrajectory(predictions12m, unit), [predictions12m, unit]);
    const activeQuotes = useMemo(() => activeQuotesFor(pipeline, quoteEquipment), [pipeline, quoteEquipment]);
    const liveQuote = activeQuotes.length ? Math.max(...activeQuotes.map((q) => q.selling_price)) : null;
    const anyInsight = hasAnyInsight(unit, { mfData, comps, trajectory });
    const hasPriceIntel = Boolean(unit.PredictedPrice && unit.Best_Sell_Price);
    const listingUrl = detail.thDetail?.listing_url || null;

    // Without a forecast, trajectory or carrying cost the left column would
    // stand empty: the cards then flow over the full width (QA BUG-6).
    const leftEmpty = unit.MedianDays == null && !predictions12mLoading && !trajectory && unit.GrossMargin == null;

    // A committed unit (sale pending, pre-sold, sold): the cards about selling
    // it (risk, forecast, carrying cost, the agent) say they do not apply; the
    // price, buyer, peer and listing context stays.
    if (committed) {
        return (
            <div className="u2i">
                <div className="u2-grid2">
                    <div className="u2-col">
                        <NotForCommitted title="Days-to-sell forecast" />
                        {predictions12mLoading && <LoadingLine>Loading price trajectory...</LoadingLine>}
                        {!predictions12mLoading && trajectory && <TrajectoryCard unit={unit} trajectory={trajectory} />}
                        <NotForCommitted title="Carrying cost & margin" id="u2-carry" />
                    </div>
                    <div className="u2-col">
                        <NotForCommitted title="Ask IronSight" />
                        <NotForCommitted title="Sell-speed risk" />
                        {hasPriceIntel && <PriceIntelCard unit={unit} liveQuote={liveQuote} />}
                        {hasQuoteInterest(unit) && <BuyerInterestCard unit={unit} goTab={goTab} />}
                        {!comps && <LoadingLine>Loading peer comparisons...</LoadingLine>}
                        <PeersCard unit={unit} comps={comps} goTab={goTab} />
                        {mfData && <ListingInsightsCard unit={unit} mfData={mfData} listingUrl={listingUrl} />}
                        {mfLoading && !mfData && <LoadingLine>Loading {SOURCE_LABELS.listingLower} data...</LoadingLine>}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="u2i">
            {hasAnomaly(unit) && <AnomalyCard unit={unit} />}
            {anyInsight && leftEmpty && (
                <div className="u2i-flow" data-testid="intel-flow">
                    <AskTeaser detail={detail} goTab={goTab} />
                    {unit.RiskTier && <RiskCard unit={unit} allUnits={allUnits} />}
                    {hasPriceIntel && <PriceIntelCard unit={unit} liveQuote={liveQuote} />}
                    {(hasQuoteInterest(unit) || hasSellSpeedData(unit)) && <BuyerInterestCard unit={unit} goTab={goTab} />}
                    {!comps && <LoadingLine>Loading peer comparisons...</LoadingLine>}
                    <PeersCard unit={unit} comps={comps} goTab={goTab} />
                    {mfData && <ListingInsightsCard unit={unit} mfData={mfData} listingUrl={listingUrl} />}
                    {mfLoading && !mfData && <LoadingLine>Loading {SOURCE_LABELS.listingLower} data...</LoadingLine>}
                </div>
            )}
            {anyInsight && !leftEmpty ? (
                <div className="u2-grid2">
                    <div className="u2-col">
                        {unit.MedianDays != null && <ForecastCard unit={unit} />}
                        {predictions12mLoading && <LoadingLine>Loading price trajectory...</LoadingLine>}
                        {!predictions12mLoading && trajectory && <TrajectoryCard unit={unit} trajectory={trajectory} />}
                        <CarryingCard unit={unit} calcPrice={calcPrice} setCalcPrice={setCalcPrice} activeQuotes={activeQuotes} />
                    </div>
                    <div className="u2-col">
                        <AskTeaser detail={detail} goTab={goTab} />
                        {unit.RiskTier && <RiskCard unit={unit} allUnits={allUnits} />}
                        {hasPriceIntel && <PriceIntelCard unit={unit} liveQuote={liveQuote} />}
                        {(hasQuoteInterest(unit) || hasSellSpeedData(unit)) && <BuyerInterestCard unit={unit} goTab={goTab} />}
                        {!comps && <LoadingLine>Loading peer comparisons...</LoadingLine>}
                        <PeersCard unit={unit} comps={comps} goTab={goTab} />
                        {mfData && <ListingInsightsCard unit={unit} mfData={mfData} listingUrl={listingUrl} />}
                        {mfLoading && !mfData && <LoadingLine>Loading {SOURCE_LABELS.listingLower} data...</LoadingLine>}
                    </div>
                </div>
            ) : !anyInsight && (
                <div className="u2-card u2i-empty">
                    <p>No AI predictions available for this unit yet.</p>
                </div>
            )}
        </div>
    );
}

IntelligenceTabV2.propTypes = { ctx: tabCtxShape.isRequired };
